/**
 * 화면 표시 선호 스토어(zustand + localStorage persist) — 글자 크기·좌측 메뉴 접힘·관심 사업 유형·공고 찾기 보기.
 * 모바일/PC 화면 선택은 탭 세션 한정이라 `stores/uiMode.ts`(sessionStorage)가 소유한다.
 * 저장소 접근 실패(사생활 보호 창·차단)는 persist 가 삼키고 기본값으로 동작한다 — 선호는 편의일 뿐 화면 동작을 막지 않는다.
 * 글자 크기는 useRootFontSize() 가 <html data-font-size> 로 반영한다(styles/theme-extensions.css 의 루트 배율).
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import {
  DEFAULT_FAVORITE_CATEGORY,
  isProgramCategoryCode,
  type ProgramCategoryCode,
} from '@/config/program-category';
import type { FinderMode, FinderView } from '@/features/finder/types';

/** 글자 크기 단계 — 기본(md)을 중심으로 작게/크게. theme-extensions.css 의 루트 font-size(87.5% · 100% · 112.5%)와 1:1. */
export type FontSizeLevel = 'sm' | 'md' | 'lg';

export const FONT_SIZE_OPTIONS: readonly { value: FontSizeLevel; label: string }[] = [
  { value: 'sm', label: '작게' },
  { value: 'md', label: '기본' },
  { value: 'lg', label: '크게' },
];

interface PreferencesState {
  /** 전체 글자 배율 단계 — rem 기반 스케일 전체가 같은 비율로 커진다. */
  fontSize: FontSizeLevel;
  setFontSize: (level: FontSizeLevel) => void;
  /** 좌측 메뉴를 아이콘 레일로 접음(데스크톱 전용 — 모바일 시트는 항상 전체). 기본 접힘 — 본문 폭 우선. */
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  /**
   * 관심 사업 유형 — 캘린더 행에서 이 유형만 강조 칩으로 보인다. null = 강조 끔.
   * 표시 선호일 뿐 목록을 거르지 않는다(거르기는 필터 바의 "관심 유형만" 토글이 URL 필터로 수행).
   */
  favoriteCategory: null | ProgramCategoryCode;
  setFavoriteCategory: (code: null | ProgramCategoryCode) => void;
  /**
   * 공고 찾기 보기 기억(specs/031 FR-003) — 모드별로 둔다(목록=표·검색=카드 기본값이 서로를 덮지 않게).
   * 브라우저별 편의 설정이며 계정 간 동기화하지 않는다. 주소의 `view` 가 있으면 그쪽이 우선한다.
   */
  finderView: Partial<Record<FinderMode, FinderView>>;
  setFinderView: (mode: FinderMode, view: FinderView) => void;
}

function isFinderView(v: unknown): v is FinderView {
  return v === 'table' || v === 'cards';
}

export const PREFERENCES_STORAGE_KEY = 'ws.prefs';

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set) => ({
      fontSize: 'md',
      setFontSize: (level) => set({ fontSize: level }),
      sidebarCollapsed: true,
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      favoriteCategory: DEFAULT_FAVORITE_CATEGORY,
      setFavoriteCategory: (code) => set({ favoriteCategory: code }),
      finderView: {},
      setFinderView: (mode, view) =>
        set((s) => ({ finderView: { ...s.finderView, [mode]: view } })),
    }),
    {
      name: PREFERENCES_STORAGE_KEY,
      // v1: 고대비 모드(highContrast) 폐기. v2: 관심 사업 유형(favoriteCategory) 추가 — 없으면 기본값.
      // v3: 공고 찾기 보기(finderView) 추가 — 없거나 형식이 틀리면 비운다(모드 기본값).
      // v4: 화면 선택(uiMode, specs/037) 추가. v5: uiMode 를 탭 세션 저장소(stores/uiMode.ts)로 이전 — 남은 값은 버린다.
      version: 5,
      migrate: (persisted) => {
        const { fontSize, sidebarCollapsed, favoriteCategory, finderView } = (persisted ??
          {}) as Partial<PreferencesState>;
        return {
          fontSize: fontSize ?? 'md',
          sidebarCollapsed: sidebarCollapsed ?? true,
          favoriteCategory:
            favoriteCategory === null || isProgramCategoryCode(favoriteCategory)
              ? favoriteCategory
              : DEFAULT_FAVORITE_CATEGORY,
          finderView: {
            ...(isFinderView(finderView?.LIST) && { LIST: finderView.LIST }),
            ...(isFinderView(finderView?.SEARCH) && { SEARCH: finderView.SEARCH }),
          },
        };
      },
      partialize: ({ fontSize, sidebarCollapsed, favoriteCategory, finderView }) => ({
        fontSize,
        sidebarCollapsed,
        favoriteCategory,
        finderView,
      }),
    },
  ),
);
